import * as React from "react";
import * as AccordionPrimitive from "@radix-ui/react-accordion";

import { cn } from "~/lib/utils";
import { ChevronRightIcon } from "~/icons/ChevronRightIcon";

function Accordion({
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Root>) {
  return <AccordionPrimitive.Root data-slot="accordion" {...props} />;
}

function AccordionItem({
  className,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Item>) {
  return (
    <AccordionPrimitive.Item
      data-slot="accordion-item"
      className={cn("mb-3 last:mb-0", className)}
      {...props}
    />
  );
}

function AccordionTrigger({
  className,
  children,
  variant = "dark",
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Trigger> & {
  variant?: "light" | "dark";
}) {
  return (
    <AccordionPrimitive.Header className="flex">
      <AccordionPrimitive.Trigger
        data-slot="accordion-trigger"
        className={cn(
          "cursor-pointer font-semibold text-lg focus-visible:border-ring focus-visible:ring-ring/50 flex items-center w-full gap-3 rounded-md transition-all outline-none focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-50 [&[data-state=open]>svg]:rotate-90",
          variant === "dark"
            ? "bg-gray-darker text-white px-3  py-[9px]"
            : "px-0 pt-3 justify-between border-t-2 border-black rounded-none",
          className
        )}
        {...props}
      >
        {variant === "dark" ? (
          <ChevronRightIcon className="text-white pointer-events-none size-6 shrink-0 transition-transform duration-200" />
        ) : null}
        {children}
        {variant === "light" ? (
          <ChevronRightIcon className="pointer-events-none size-6 shrink-0 transition-transform duration-200" />
        ) : null}
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  );
}

function AccordionContent({
  className,
  children,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Content>) {
  return (
    <AccordionPrimitive.Content
      data-slot="accordion-content"
      className={cn(
        "data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down overflow-hidden",
        "bg-white rounded-b-md p-3 mx-1.5 mb-1.5"
      )}
      {...props}
    >
      <div className={cn("pt-0 pb-4", className)}>{children}</div>
    </AccordionPrimitive.Content>
  );
}

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
